import React from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../store/hooks';
import { apiSlice } from '../api/apiSlice';
import { useGetRunningTasksQuery } from '../api/variantApi';
import { selectUsername } from '../authentication/redux/selectors';
import type { RunningTask } from '../variant_workflow/redux';
import {
  REFRESH_PENDING_TASK_ID_PREFIX,
  RUNNING_TASK_POLLING_INTERVAL,
  RUNNING_TASK_TYPES,
  VARIANT_CREATION_TASK_TYPES,
} from './constants';

const VARIANT_DATA_TAGS = [
  'VariantDetail',
  'VariantStages',
  'VariantComments',
  'VariantHistory',
  'VariantSamples',
  'VariantDatabases',
  'ClinvarRecord',
  'ClinvarAssertions',
  'MedicalInvestigations',
  'VariantFrequencies',
  'MaxAFScorings',
  'MolecularImpactHeader',
  'VariantPredictions',
  'SpliceSiteAlteration',
  'ProteinDomains',
  'OtherVariants',
  'InterpretationZone',
  'VariantArguments',
  'InterpretationSnapshots',
  'GeneFactsOverview',
  'VariantReferences',
  'VariantLinkouts',
  'VariantTranscripts',
] as const;

const RunningTaskMonitor = () => {
  const dispatch = useAppDispatch();
  const username = useSelector(selectUsername);
  const previousTasks = React.useRef<RunningTask[] | null>(null);
  const runningRefreshTaskIds = React.useRef<Set<string>>(new Set());
  const { data = [], isSuccess } = useGetRunningTasksQuery(undefined, {
    pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
    skip: !username,
  });

  React.useEffect(() => {
    if (!isSuccess) {
      return;
    }

    if (previousTasks.current) {
      const currentTasksById = new Map(
        data.map((task) => [task.task_id, task]),
      );
      // Completion is detected via status transition: a task is done once its
      // status left "running" (completed rows are kept by the server now).
      const completedTasks = previousTasks.current.filter((task) => {
        if (task.status !== 'running') {
          return false;
        }
        const current = currentTasksById.get(task.task_id);
        return current ? current.status !== 'running' : false;
      });

      if (
        completedTasks.some((task) =>
          VARIANT_CREATION_TASK_TYPES.includes(task.task_type),
        )
      ) {
        dispatch(apiSlice.util.invalidateTags(['Variants']));
      }
    }

    // Refresh tasks are tracked by id instead of by the previous snapshot, so
    // the completion is also noticed when the task row changes between two
    // polls or is already gone from the response.
    const finishedRefreshTaskIds = Array.from(
      runningRefreshTaskIds.current,
    ).filter(
      (taskId) =>
        data.find((task) => task.task_id === taskId)?.status !== 'running',
    );
    finishedRefreshTaskIds.forEach((taskId) =>
      runningRefreshTaskIds.current.delete(taskId),
    );
    data.forEach((task) => {
      if (
        task.task_type === RUNNING_TASK_TYPES.REFRESH_AND_RECALCULATE &&
        task.status === 'running' &&
        !task.task_id.startsWith(REFRESH_PENDING_TASK_ID_PREFIX)
      ) {
        runningRefreshTaskIds.current.add(task.task_id);
      }
    });
    if (finishedRefreshTaskIds.length > 0) {
      dispatch(apiSlice.util.invalidateTags([...VARIANT_DATA_TAGS]));
    }

    previousTasks.current = data;
  }, [data, dispatch, isSuccess]);

  return null;
};

export default RunningTaskMonitor;
